import { useMutation } from '@tanstack/react-query';

import * as ConfiguratorStore from '@Stores/Configurator';
import * as Filtrator from '@Stores/Filtrator';
import * as PageProductStore from '@Stores/PageProduct';
import Configurator from '@Api/Configurator';
import { useRequest } from '@Contexts/Request';
import useKeys from './useKeys';

import type { ConfiguratorData } from '@Pages/PageProduct';
import type { ParamsData } from '@Api/Configurator/getFilterResult';

const useApplyFilters = (id: number) => {
  const { queryClient, region } = useRequest();
  const selectedPvs = ConfiguratorStore.useSelectedParams();
  const {
    product: { modelId },
  } = PageProductStore.useData();

  const keys = useKeys(id, [...selectedPvs]);
  const entityId = 'configurator-textile';
  const selected = Filtrator.useSelected(entityId);
  const filtrator = Filtrator.useFiltrator(entityId);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async () => {
      const params: ParamsData = {};

      Object.keys(selected.parameters).map((parameter) => {
        if (parameter === 'price') {
          return (params[parameter] = {
            min: +selected.parameters[parameter].default[0],
            max: +selected.parameters[parameter].default[1],
          });
        }
        return (params[parameter] = selected.parameters[parameter].default);
      });

      const res = await Configurator.getFilterResult({
        shopProductId: id,
        params,
      });
      return res;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['selected-filters', modelId, region], () => ({
        ...data,
        filters: filtrator,
      }));
      const inStock = +filtrator.parameters.in_stock?.default === 1;

      queryClient.setQueryData(keys, (prev: ConfiguratorData) => {
        const isBasic = prev.configuration.isBasic;

        return {
          ...prev,
          fabricCollections: prev.fabricCollections.map((fc) => {
            return { ...fc, hidden: !data.fabricCollections.some((dfc) => dfc.id === fc.id) };
          }),
          fabricFilters: filtrator,
          parameterValues: [
            ...prev.parameterValues
              .filter((pv) => pv.type !== 'fabric')
              .map((pv) => {
                return {
                  ...pv,
                  calcDisabled: inStock && isBasic ? pv.disabled : false,
                  selected: inStock && isBasic ? !pv.disabled : pv.selected,
                };
              }),
            ...prev.parameterValues
              .filter((pv) => pv.type === 'fabric')
              .map((item) => {
                return {
                  ...item,
                  hidden: !data.parameterValues.some(
                    (pv) =>
                      pv.value === item.value && pv.tags?.collectionId === item.tags?.collectionId,
                  ),
                };
              }),
          ],
          countFabrics: data.parameterValues.length,
        };
      });
    },
  });

  return {
    applyFilters: mutateAsync,
    isPending,
  };
};

export default useApplyFilters;
